<template>
    <div class="pt-4">
        <nav class="flex items-center gap-1 text-sm font-semibold text-gray-500">
            <ULink to="/telemedicine/doctors" class="hover:text-gray-700">Doctors</ULink>
            <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
            <ULink v-if="profile" :to="`/telemedicine/doctors/${profile.id}`" class="max-w-xs truncate hover:text-gray-700">{{ displayName }}</ULink>
            <USkeleton v-else-if="loading" class="h-4 w-28" />
            <template v-if="profile || loading">
                <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                <span class="text-gray-700">Schedule</span>
            </template>
        </nav>

        <div v-if="loading" class="mt-3 space-y-4">
            <div class="flex items-center gap-4">
                <USkeleton class="size-12 rounded-lg" />
                <div class="space-y-2">
                    <USkeleton class="h-7 w-64" />
                    <USkeleton class="h-4 w-40" />
                </div>
            </div>
            <USkeleton class="h-14" />
            <div class="grid gap-4 xl:grid-cols-2">
                <USkeleton class="h-[28rem]" />
                <USkeleton class="h-[28rem]" />
            </div>
        </div>

        <div v-else-if="loadError" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-warning-duotone" class="size-8 text-red-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">Couldn't load this doctor's schedule</h2>
            <p class="mt-1 text-sm text-gray-500">{{ loadError }}</p>
            <UButton @click="retry()" :loading="retrying" color="white" class="mt-5" icon="i-ph-arrow-clockwise-bold">Retry</UButton>
        </div>

        <div v-else-if="!profile" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-stethoscope-duotone" class="size-8 text-gray-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">Doctor not found</h2>
            <p class="mt-1 text-sm text-gray-500">It may have been removed, or the link is incorrect.</p>
            <UButton to="/telemedicine/doctors" color="white" class="mt-5" icon="i-ph-arrow-left-bold">Back to doctors</UButton>
        </div>

        <template v-else>
            <div class="mt-2 mb-4 flex flex-wrap items-center justify-between gap-4">
                <div class="flex min-w-0 items-center gap-4">
                    <UAvatar :src="photoUrl" :alt="profile.full_name" size="xl" :ui="{ rounded: 'rounded-lg' }" />
                    <div class="min-w-0 space-y-0.5">
                        <div class="flex items-center gap-3">
                            <h1 class="truncate text-2xl font-semibold text-gray-700">{{ displayName }}</h1>
                            <TelemedicineDoctorStatusBadge :status="profile.status" />
                        </div>
                        <p class="flex items-center gap-1.5 text-sm text-gray-500">
                            <UIcon name="i-ph-calendar-dots-duotone" class="size-4 text-gray-400" />
                            <span>Schedule</span>
                            <template v-if="profile.specialty">
                                <span class="text-gray-300">·</span>
                                <span>{{ profile.specialty.name_en }}</span>
                            </template>
                        </p>
                    </div>
                </div>
                <UButton :to="`/telemedicine/doctors/${profile.id}`" color="white" icon="i-ph-identification-card-duotone">
                    Profile
                </UButton>
            </div>

            <UAlert
                v-if="profile.status !== DoctorStatus.published"
                color="amber"
                variant="subtle"
                icon="i-ph-eye-slash-duotone"
                :title="profile.status === DoctorStatus.suspended ? 'Suspended: patients can\'t book this doctor.' : 'Not published yet: patients can\'t book this doctor.'"
                description="The preview shows the slots patients will see once the doctor is live."
                class="mb-4"
            />
            <UAlert
                v-else-if="profile.specialty?.active === false"
                color="amber"
                variant="subtle"
                icon="i-ph-eye-slash-duotone"
                title="Hidden from the app: the specialty is inactive."
                class="mb-4"
            />

            <TelemedicineScheduleEditor
                :doctor-id="profile.id"
                :timezone="profile.timezone"
                :duration-min="profile.session_duration_min"
                :buffer-min="profile.buffer_min"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { DoctorStatus, type DoctorProfile } from '~/types/telemedicine'
import { doctorPhotoUrl } from '~/utils/telemedicineDoctor'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.telemedicineAdmin] })

type ScheduleProfile = Pick<DoctorProfile, 'id' | 'title' | 'full_name' | 'photo_path' | 'specialty' | 'timezone' | 'session_duration_min' | 'buffer_min' | 'status'>

const route = useRoute()
const client = useSupabaseClient()
const config = useRuntimeConfig()
const toast = useToast()

const id = computed(() => route.params.id as string)
const loading = ref(true)
const retrying = ref(false)
const loadError = ref<string>()
const profile = ref<ScheduleProfile>()

const displayName = computed(() => (profile.value ? [profile.value.title, profile.value.full_name].filter(Boolean).join(' ') : ''))
const photoUrl = computed(() => (profile.value ? doctorPhotoUrl(config.public.supabase.url, profile.value) ?? undefined : undefined))

useHead({ title: computed(() => (profile.value ? `Telemedicine – ${displayName.value} – Schedule` : 'Telemedicine – Schedule')) })

onMounted(async () => {
    try {
        await load()
    } finally {
        loading.value = false
    }
})

async function load() {
    const { data, error } = await client.from('doctor_profiles')
        .select('id, title, full_name, photo_path, timezone, session_duration_min, buffer_min, status, specialty:specialties(id, name_en, name_ar, active)')
        .eq('id', id.value)
        .maybeSingle()
    if (error) {
        loadError.value = error.message
        toast.add({ title: 'Could not load the doctor', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    loadError.value = undefined
    profile.value = (data ?? undefined) as unknown as ScheduleProfile | undefined
}

async function retry() {
    retrying.value = true
    try {
        await load()
    } finally {
        retrying.value = false
    }
}
</script>
